import Vue from "vue";
import { pbkdf2 } from "pbkdf2";
import { recordScreenLock } from "@/api/systemLockApi";

const KEY = "lcsj-system-lock";
const REENTRY_KEY = "lcsj-system-lock-reentry";
const ITERATIONS = 100000;
const empty = () => ({ owner: "", enabled: true, locked: false, salt: "", hash: "", failures: 0, retryAt: 0 });
let settings = empty();
let requestId = 0;
let initialEntry = true;
try {
	const saved = JSON.parse(sessionStorage.getItem(KEY));
	if (saved && saved.owner && (!saved.locked || (/^[a-f0-9]{32}$/.test(saved.salt) && /^[a-f0-9]{64}$/.test(saved.hash)))) settings = { ...settings, ...saved };
} catch (_) { /* No saved session. */ }

export const lockState = Vue.observable({ enabled: settings.enabled, locked: settings.locked, preparing: false, retryAt: settings.retryAt });
const owner = () => localStorage.getItem("logintoken") && localStorage.getItem("userId");

function save(next) {
	// Persist before changing the UI, so a storage failure cannot create a lock that disappears on refresh.
	// Only the owner marker survives closing the tab; lock credentials remain session-only.
	if (next.locked) localStorage.setItem(REENTRY_KEY, next.owner);
	else localStorage.removeItem(REENTRY_KEY);
	sessionStorage.setItem(KEY, JSON.stringify(next));
	settings = next;
	Object.assign(lockState, { enabled: next.enabled, locked: next.locked, retryAt: next.retryAt });
}

export function clearSystemLock() {
	requestId++;
	sessionStorage.removeItem(KEY);
	localStorage.removeItem(REENTRY_KEY);
	settings = empty();
	Object.assign(lockState, { enabled: true, locked: false, preparing: false, retryAt: 0 });
}

// Run once before normal route authorization, including direct visits to the login page.
export function requiresLoginOnEntry() {
	if (!initialEntry) return false;
	initialEntry = false;
	const user = owner();
	return !!user && ((settings.locked && settings.owner === user) || localStorage.getItem(REENTRY_KEY) === user);
}

export function syncLockSession() {
	if (settings.owner && settings.owner !== owner()) clearSystemLock();
}

function derive(password, salt) {
	return new Promise((resolve, reject) => {
		// Uses Web Crypto when available, with a browser implementation for HTTP intranet deployments.
		pbkdf2(password, salt, ITERATIONS, 32, "sha256", (error, key) => error ? reject(error) : resolve(key.toString("hex")));
	});
}

async function verify(password) {
	const snapshot = settings;
	if (snapshot.retryAt > Date.now()) throw new Error("尝试次数过多，请稍后再试");
	const hash = await derive(password, snapshot.salt);
	if (snapshot !== settings) throw new Error("锁屏设置已改变，请重试");
	if (hash !== snapshot.hash) {
		const failures = snapshot.failures + 1;
		save({ ...snapshot, failures: failures >= 5 ? 0 : failures, retryAt: failures >= 5 ? Date.now() + 30000 : 0 });
		throw new Error(failures >= 5 ? "密码错误，请等待 30 秒后重试" : "锁屏密码错误，请重新输入");
	}
}

export function setLockEnabled(enabled) {
 syncLockSession();
 if (settings.locked) throw new Error("请先解锁系统");
 if (!owner()) throw new Error("请先登录系统");
 cancelSystemLock();
 save({ ...empty(), owner: owner(), enabled });
}
export function requestSystemLock() {
 syncLockSession();
 if (!owner() || !settings.enabled || settings.locked || lockState.preparing) return false;
 requestId++;
 lockState.preparing = true;
 return true;
}
export function cancelSystemLock() { requestId++; lockState.preparing = false; }
export async function lockSystem(password) {
 const user = owner(), currentRequest = requestId;
 if (!user || !settings.enabled || !lockState.preparing || settings.locked) return false;
 if (password.length < 6 || password.length > 32) throw new Error("锁屏密码需为 6–32 位");
 const random = new Uint8Array(16);
 window.crypto.getRandomValues(random);
 const salt = Array.from(random, n => n.toString(16).padStart(2, "0")).join("");
 const hash = await derive(password, salt);
 if (currentRequest !== requestId || !lockState.preparing || owner() !== user) throw new Error("锁屏设置已取消或登录状态已改变");
 save({ ...empty(), owner: user, enabled: true, locked: true, salt, hash });
 lockState.preparing = false;
 // Persist the audit event in the background without messages or blocking unlock.
 Promise.resolve().then(() => recordScreenLock()).catch(() => {});
 return true;
}

export async function unlockSystem(password) {
	if (!settings.locked) return;
	await verify(password);
	save({ ...empty(), owner: settings.owner, enabled: settings.enabled });
}
